import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { ViralSoundsRankedListItem } from '@components/Landing/sections/ViralSounds/ViralSoundsRankedListItem';
import { LandingRankedList } from '@components/Landing/shared/LandingRankedList';
import { DownloadViralScreenshotModal } from '@components/modals/DownloadScreenshotModal';
import { ModalType, useModal } from '@context/modal';
import { ToastContext } from '@context/toast';
import type { ExecutionResultWithData, ViralSoundsLandingQuery } from '@graphql/reactQuery';
import {
  getFragment,
  useQuery,
  ViralSoundsLandingDocument,
  ViralSoundsRankedListItemFragmentDoc,
} from '@graphql/reactQuery';
import { logError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY, NAV_LINKS } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ViralSoundsLanding($pagination: ReleaseChartRankCursorConnectionArgs!) {
    currentReleaseChart {
      id
      chartRanks(pagination: $pagination) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...ViralSoundsRankedListItem
          }
        }
      }
    }
  }
`);

export function ViralSounds({
  initialData,
}: {
  initialData: ExecutionResultWithData<ViralSoundsLandingQuery>;
}) {
  const { openToast } = ToastContext.useContainer();
  const { openModal } = useModal();

  /**
   * Get viral sounds from initialData, do not network
   */
  const { data: viralSoundsQueryData } = useQuery(ViralSoundsLandingDocument, {
    initialData,
    variables: {
      pagination: { first: 10, after: null },
    },
    onError: error => {
      if (error) {
        // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
        openToast({
          text: 'Something went wrong loading viral sounds. Please try again later.',
          variant: 'error',
        });
      }
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  /**
   * We need to pass allReleases through so the invididual
   * items can know what to queue next when the play button
   * is clicked
   */
  const allReleases = React.useMemo(
    () =>
      viralSoundsQueryData?.data.currentReleaseChart.chartRanks.edges.map(({ node }) => {
        const chartRankNode = getFragment(ViralSoundsRankedListItemFragmentDoc, node);
        return chartRankNode.release;
      }) || [],
    [viralSoundsQueryData],
  );

  /**
   * Restructure as LandingRankedListItem
   */
  const landingRankedListItems = React.useMemo(() => {
    return viralSoundsQueryData?.data.currentReleaseChart.chartRanks.edges.map(({ node }) => {
      const chartRankNode = getFragment(ViralSoundsRankedListItemFragmentDoc, node);
      return (
        <ViralSoundsRankedListItem
          key={`ViralSoundsRankedListItem-${chartRankNode.id}`}
          chartRank={chartRankNode}
          allReleases={allReleases}
        />
      );
    });
  }, [viralSoundsQueryData, allReleases]);

  /**
   * If there are no viral sounds (for whatever reason), do not render this section
   */
  if (!landingRankedListItems || !viralSoundsQueryData) {
    logError({
      error: new Error('No viral sounds'),
      message: 'Viral sounds on landing page is empty',
      feature: 'LANDING_PAGE',
      errorType: 'VIRAL_SOUNDS_FETCH_ERROR',
      level: 'fatal',
      action: 'Viral sounds on landing page is empty',
      isArtistOnlyPage: false,
    });
    return null;
  }

  return (
    <LandingRankedList
      headingProps={{
        header: 'Viral Sounds This Week',
        pathnameUri: NAV_LINKS.Charts,
        onDownloadScreenshotClicked: () => {
          trackClickEvent({
            event: 'Share Viral Sounds Clicked',
            properties: { sourceComponent: 'Landing page' },
          });
          openModal(ModalType.SHARE, {
            body: (
              <DownloadViralScreenshotModal
                chartId={viralSoundsQueryData.data.currentReleaseChart.id}
              />
            ),
          });
        },
      }}
      isLoading={false}
      imageRadius="rounded-md"
      id="viral-sounds"
      numCols={2}
      numRows={5}
      items={landingRankedListItems}
    />
  );
}
